import { SxProps } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { Box, Grid, useTheme } from '@mui/material';
import AppCustomIcon from '../../../../../../../app/features/icon/AppIcon';
import { ReactNode, useEffect, useState } from 'react';
import React from 'react';
import AppFormatAmount from '../../../../../../../app/features/format/AppFormatAmount';

import CAppPaper from '../../../../../../../app/components/CAppPaper';
import investmentTypeLang from '../../../langs/investmentTypeLang';
import langInvestWordLang from '../../../../../langs/langInvestWordLang';
import AppFormatX from '../../../../../../../app/features/format/AppFormatX';
import AppFormatPercent from '../../../../../../../app/features/format/AppFormatPercent';
import langInvestFundPerformancesVentureCapitalLang from '../../../../../langs/langInvestFundPerformancesVentureCapitalLang';
import IInvestKpis from '../../../../../interfaces/IInvestKpis';
import CAppInfoTooltip from '../../../../../../../app/components/CAppInfoTooltip';
import { TInvestIsNTag } from '../../../../../types/TInvestIsNTag';
import { TInvestFundCurrency } from '../../../../../types/fund/TInvestFundCurrency';

type CSynthesisIndicatorsProps = {
	kpis: IInvestKpis;
	variant: 'elevation' | 'outlined';
	currency: TInvestFundCurrency;
};
const CSynthesisIndicators = (props: CSynthesisIndicatorsProps) => {
	const { kpis, variant, currency } = props;

	const { t } = useTranslation();
	const theme = useTheme();

	const [irrGross, setIrrGross] = useState<number>();
	const [irrGrossIsN, setIrrGrossIsN] = useState<TInvestIsNTag>();
	const [fees, setFees] = useState<number>();
	const [irr, setIrr] = useState<number>();
	const [irrIsNs, setIrrIsNs] = useState<boolean>();
	const [tvpi, setTvpi] = useState<number>();
	const [dpi, setDpi] = useState<number>();
	const [rvpi, setRvpi] = useState<number>();
	useEffect(() => {
		if (kpis) {
			setIrrGross(kpis.irrGross ?? undefined);
			setIrrGrossIsN(kpis.irrGrossIsN ?? undefined);
			setFees(kpis.fees ?? undefined);
			setIrr(kpis.irr ?? undefined);
			setIrrIsNs(kpis.irrIsNs ?? undefined);
			setTvpi(kpis.tvpi ?? undefined);
			setDpi(kpis.dpi ?? undefined);
			setRvpi(kpis.rvpi ?? undefined);
		}

		return () => {
			setIrrGross(undefined);
			setIrrGrossIsN(undefined);
			setFees(undefined);
			setIrr(undefined);
			setIrrIsNs(undefined);
			setTvpi(undefined);
			setDpi(undefined);
			setRvpi(undefined);
		};
	}, [kpis]);

	const styleTitle = {
		color: theme.palette.secondary.main,
		verticalAlign: 'text-bottom',
		boxSizing: 'inherit',
		textAlign: 'left',
		alignItems: 'center',
		display: 'inline-flex',
		fontWeight: '500',
		fontSize: '1.25rem',
		'& .icon': {
			marginRight: '10px',
		},
	};
	const styleKPI = {
		padding: '30px',
		backgroundColor: theme.palette.primary.main,
		border: '0px',
		borderRadius: '10px',
		textAlign: 'left',
		'& .title': styleTitle,
		'& .list': {
			color: 'white',
			textAlign: 'left',
			alignItems: 'center',
			padding: '10px 0px 0px 0px',
			fontWeight: 'normal',
			'& .item': {
				color: 'white',
				textAlign: 'left',
				alignItems: 'center',
				padding: '12px 0px',
				fontWeight: 'normal',
				'& .value': {
					textAlign: 'right',
					fontWeight: '600',
					fontSize: '1.25rem',
				},
				'& .label': {
					textAlign: 'left',
				},
			},
			'& .item:last-child': {
				paddingBottom: '0px',
			},
		},
	} as SxProps;

	return (
		<CAppPaper variant={variant ?? 'outlined'} color="primary" sx={styleKPI}>
			<Box className="title">
				<AppCustomIcon tag="kpi_performance" className="icon" /> {t(investmentTypeLang.indicators.tag)}
			</Box>
			<Box className="list performance-kpis-list">
				<InvestmentTypeLinePerformanceKpi
					lineLabel={<>{t(langInvestFundPerformancesVentureCapitalLang.tvpi.tag)}</>}
					lineValue={<AppFormatX textIfNull={t(langInvestWordLang.nc.tag)} value={tvpi} />}
				/>
				<InvestmentTypeLinePerformanceKpi
					lineLabel={t(langInvestFundPerformancesVentureCapitalLang.dpi.tag)}
					lineValue={<AppFormatX textIfNull={t(langInvestWordLang.nc.tag)} value={dpi} />}
				/>
				<InvestmentTypeLinePerformanceKpi
					lineLabel={t(langInvestFundPerformancesVentureCapitalLang.rvpi.tag)}
					lineValue={<AppFormatX value={rvpi} textIfNull={t(langInvestWordLang.nc.tag)} />}
				/>
				<InvestmentTypeLinePerformanceKpi
					lineLabel={
						<>
							{t(langInvestFundPerformancesVentureCapitalLang.irr_gross.tag)}
							<CAppInfoTooltip
								title={t(langInvestFundPerformancesVentureCapitalLang.irr_gross_tooltip.tag)}
							/>
						</>
					}
					lineValue={
						<AppFormatPercent
							value={irrGross}
							isN={irrGrossIsN}
							textIfNull={t(langInvestWordLang.na.tag)}
						/>
					}
				/>
				<InvestmentTypeLinePerformanceKpi
					lineLabel={t(langInvestFundPerformancesVentureCapitalLang.fees.tag)}
					lineValue={
						<AppFormatAmount value={fees} textIfNull={t(langInvestWordLang.nc.tag)} currency={currency} />
					}
				/>
				<InvestmentTypeLinePerformanceKpi
					lineLabel={t(langInvestFundPerformancesVentureCapitalLang.irr.tag)}
					lineValue={
						<AppFormatPercent value={irr} isNS={irrIsNs} textIfNull={t(langInvestWordLang.nc.tag)} />
					}
				/>
			</Box>
		</CAppPaper>
	);
};

type LinePerformanceKpiProps = {
	lineLabel: string | ReactNode;
	lineValue: string | ReactNode;
};
const InvestmentTypeLinePerformanceKpi = (props: LinePerformanceKpiProps) => {
	const { lineLabel, lineValue } = props;

	return (
		<Box className="item">
			<Grid container spacing={3}>
				<Grid item xs={8} className="label">
					{lineLabel}
				</Grid>
				<Grid item xs={4} className="value">
					{lineValue}
				</Grid>
			</Grid>
		</Box>
	);
};

export default CSynthesisIndicators;
